import { useContext, useState } from 'react';

import { resetPassword, confirmResetPassword } from 'aws-amplify/auth';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import TextButton from '../../../../../shared/components/Buttons/TextButton/TextButton';
import Toast from '../../../../../shared/components/Globals/Toast/Toast';
import { LOGIN_ROUTE, RECAPTCHA_SITE_KEY } from '../../../../../shared/constants/auth';
import { ARROW_LEFT_ICON } from '../../../../../shared/constants/images';
import { CLICK_ACTION, RESTORE_PASSWORD } from '../../../../../shared/constants/tagManager';
import { GENERIC_ERROR_TOAST } from '../../../../../shared/constants/toast';
import { AuthContext } from '../../../../../shared/hooks/useAuth/useAuth';
import TagManagerService from '../../../../../shared/services/gtm/tagManagerService';
import { TRANSLATIONS_AUTH } from '../../../../../shared/translations/auth';
import AuthError from '../../../sections/AuthError/AuthError';
import CreatePassword from '../../../sections/CreatePassword/CreatePassword';
import VerificationCode from '../../../sections/VerificationCode/VerificationCode';
import './ForgotPasswordCard.scss';

interface ForgotPasswordCardProps {
  email: string;
}

const ForgotPasswordCard = ({ email }: ForgotPasswordCardProps) => {
  const [step, setStep] = useState(0);
  const [verificationCode, setVerificationCode] = useState('');
  const [showErrorCard, setShowErrorCard] = useState(false);
  const [errorToast, setErrorToast] = useState(false);
  const [loading, setLoading] = useState(false);
  const [inputError, setInputError] = useState(false);

  const { sessionLogin } = useContext(AuthContext);

  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleChangeStep = (newStep: number) => {
    setStep(newStep);
  };

  const handleGoBack = () => {
    if (step === 0) {
      navigate(LOGIN_ROUTE);
      return;
    }
    setStep(step - 1);
  };

  const backToLogin = () => {
    navigate(LOGIN_ROUTE);
  };

  const handleSubmitVerificationCode = (code: string) => {
    setVerificationCode(code);
    setStep(1);
  };

  const handleBackFromErrorCard = () => {
    setStep(0);
    setShowErrorCard(false);
  };

  const resetCustomError = () => {
    setInputError(false);
  };

  const resendEmail = async () => {
    try {
      await resetPassword({ username: email });
      setStep(0);
      setShowErrorCard(false);
    } catch {
      setErrorToast(true);
    }
  };

  const handleLogin = async (userPassword: string) => {
    return new Promise<void>((resolve, reject) => {
      globalThis.grecaptcha.ready(async () => {
        try {
          const recaptchaToken = await globalThis.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action: 'submit' });
          await sessionLogin(email, userPassword, recaptchaToken);
          resolve();
        } catch (error) {
          reject(error);
        }
      });
    });
  };

  const handleResetPassword = async (userPassword: string) => {
    try {
      setLoading(true);
      TagManagerService.pushEvent(CLICK_ACTION, RESTORE_PASSWORD);
      await confirmResetPassword({ username: email, confirmationCode: verificationCode, newPassword: userPassword });
      await handleLogin(userPassword);
    } catch (error: unknown) {
      const errorString = String(error);
      if (errorString.includes('CodeMismatchException')) {
        setShowErrorCard(true);
      } else if (errorString.includes('PasswordHistoryPolicyViolationException')) {
        setInputError(true);
      } else {
        setErrorToast(true);
      }
    } finally {
      setLoading(false);
    }
  };

  if (showErrorCard) {
    return (
      <AuthError
        title={TRANSLATIONS_AUTH.passwordRecoveryError.title}
        description={TRANSLATIONS_AUTH.passwordRecoveryError.description}
        primaryButton={{
          text: TRANSLATIONS_AUTH.passwordRecoveryError.primaryButton,
          action: handleBackFromErrorCard
        }}
        secondaryButton={{
          text: TRANSLATIONS_AUTH.passwordRecoveryError.secondaryButton,
          action: resendEmail
        }}
        handleGoBack={handleBackFromErrorCard}
      />
    );
  }

  return (
    <div className="forgot-password">
      <div className="auth__go-back">
        <TextButton variant="primary" action={handleGoBack} iconLeft={ARROW_LEFT_ICON}>
          {t(TRANSLATIONS_AUTH.goBack)}
        </TextButton>
      </div>
      <div className="forgot-password__sections">
        <div
          className={`forgot-password__section ${step === 0 ? 'forgot-password__section--show' : 'forgot-password__section--hide'}`}
        >
          <VerificationCode
            email={email}
            codeType="password-reset"
            handleGoBack={backToLogin}
            handleNext={handleSubmitVerificationCode}
          />
        </div>
        <div
          className={`forgot-password__section ${step === 1 ? 'forgot-password__section--show' : 'forgot-password__section--hide'}`}
        >
          <CreatePassword
            verificationCode={verificationCode}
            email={email}
            handleGoBack={handleChangeStep}
            onSubmit={handleResetPassword}
            loading={loading}
            customError={inputError ? TRANSLATIONS_AUTH.forgotPassword.samePass : undefined}
            resetCustomError={resetCustomError}
          />
        </div>
      </div>
      <Toast
        message={t(GENERIC_ERROR_TOAST.message)}
        variant={GENERIC_ERROR_TOAST.variant}
        open={errorToast}
        onClose={() => setErrorToast(false)}
      />
    </div>
  );
};

export default ForgotPasswordCard;
